/**
 * React Query hooks for Events
 * Provides automatic caching, refetching, real-time updates, and cache invalidation
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRealtimeSubscription } from "./useRealtimeSubscription";
import * as eventsService from "../services/events.service";
import { useAuth } from "../contexts/AuthContext";
import type { Event } from "../types/event.types";
import type { UUID, PaginationParams } from "../types/common.types";

/**
 * Query key factory for events
 */
export const eventKeys = {
  all: ["events"] as const,
  lists: () => [...eventKeys.all, "list"] as const,
  list: (params: PaginationParams) => [...eventKeys.lists(), params] as const,
  details: () => [...eventKeys.all, "detail"] as const,
  detail: (id: UUID) => [...eventKeys.details(), id] as const,
  byOrg: (orgId: UUID) => [...eventKeys.all, "org", orgId] as const,
  upcoming: () => [...eventKeys.all, "upcoming"] as const,
};

/**
 * Hook to fetch events list (no longer paginated - filters instead)
 * Automatically updates when events table changes in real-time
 */
export function useEvents(options?: { enableRealtime?: boolean }) {
  const { user } = useAuth();
  const { enableRealtime = true } = options || {};

  const query = useQuery({
    queryKey: eventKeys.lists(),
    queryFn: () => eventsService.getEvents(),
    // Stale time: 30 seconds - data is considered fresh for this duration
    staleTime: 30 * 1000,
    // Cache time: 5 minutes - data stays in cache for this duration
    gcTime: 5 * 60 * 1000,
  });

  // Subscribe to real-time updates on the events table
  useRealtimeSubscription({
    table: "events",
    queryKeys: [eventKeys.lists(), eventKeys.upcoming()],
    enabled: enableRealtime && !!user,
    onInsert: (event) => {
      console.log("[Events] New event created:", event.title || event.id);
    },
    onUpdate: ({ old: _oldEvent, new: newEvent }) => {
      console.log("[Events] Event updated:", newEvent.title || newEvent.id);
    },
    onDelete: (event) => {
      console.log("[Events] Event deleted:", event.id);
    },
  });

  return query;
}

/**
 * Hook to fetch a single event by ID
 */
export function useEvent(id: UUID) {
  return useQuery({
    queryKey: eventKeys.detail(id),
    queryFn: () => eventsService.getEventById(id),
    enabled: !!id,
  });
}

/**
 * Hook to fetch events by organization
 * Note: Implement getEventsByOrganization in events.service.ts when needed
 */
export function useEventsByOrganization(orgId: UUID) {
  return useQuery({
    queryKey: eventKeys.byOrg(orgId),
    queryFn: () => eventsService.getEvents({ page: 1, limit: 1000 }), // Temporary - filter by orgId when service function is implemented
    enabled: !!orgId && false, // Disabled until service function is implemented
  });
}

/**
 * Hook to create a new event
 */
export function useCreateEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      // Optional client-supplied id enables idempotent create-on-retry.
      event: Omit<Event, "id" | "created_at" | "updated_at"> & { id?: string },
    ) => eventsService.createEvent(event),
    onSuccess: () => {
      // Invalidate all event-related queries (covers both ['events', 'list'] and ['events', {...}])
      queryClient.invalidateQueries({ queryKey: eventKeys.all });
    },
  });
}

/**
 * Hook to update an existing event
 */
export function useUpdateEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: UUID; updates: Partial<Event> }) =>
      eventsService.updateEvent(id, updates),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: eventKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: eventKeys.lists() });
      queryClient.invalidateQueries({ queryKey: eventKeys.upcoming() });
      if (data.organization_id) {
        queryClient.invalidateQueries({
          queryKey: eventKeys.byOrg(data.organization_id),
        });
      }
    },
    onError: (error, { id }) => {
      // A date-model save whose dates committed before the other fields failed: the event DID change, so
      // refresh everything that shows it (the caller displays the "Dates were saved, but ..." message).
      if (eventsService.isEventPartialSaveError(error)) {
        queryClient.invalidateQueries({ queryKey: eventKeys.all });
        queryClient.invalidateQueries({ queryKey: eventKeys.detail(id) });
      }
    },
  });
}

/**
 * Hook to delete an event
 */
export function useDeleteEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: UUID) => eventsService.deleteEvent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventKeys.all });
    },
  });
}
